import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  Input,
  Select,
  useToast,
  Tag,
  TagLabel,
  TagCloseButton,
  HStack,
  VStack,
  Text,
  Switch,
  Checkbox,
  Alert,
  AlertIcon,
  Center,
  Spinner,
  Box,
  Flex,
  SimpleGrid
} from "@chakra-ui/react";
import { FaCloudUploadAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { Panel } from "../../../components/Portal/ui";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX
} from "../../../components/Portal/portalModalChrome";

// Tiny uppercase field label used inside detail Panels (template `.label`).
const FieldLabel = ({ children, ...rest }) => (
  <Text
    as="label"
    fontSize="10px"
    textTransform="uppercase"
    fontWeight="800"
    letterSpacing="0.055em"
    color="portal.muted"
    sx={{ marginBottom: "4px !important" }}
    {...rest}
  >
    {children}
  </Text>
);

// Section heading inside the evaluation-settings Panel (technical/functional/
// business skill groups). Slightly larger than FieldLabel for the matrix groups.
const SectionLabel = ({ children }) => (
  <Text
    fontSize="11px"
    textTransform="uppercase"
    fontWeight="900"
    letterSpacing="0.055em"
    color="portal.text"
    sx={{ marginBottom: "6px !important" }}
  >
    {children}
  </Text>
);

// Compact select styling shared by the per-skill difficulty/importance pickers.
// Kept dense (28px) to match the row rhythm; padding/line-height forced with
// !important so the global bare-`select` rule cannot inflate them.
const COMPACT_SELECT_SX = {
  paddingTop: "0 !important",
  paddingBottom: "0 !important",
  lineHeight: "28px !important",
  height: "28px !important"
};

const ConsultantAssessmentModal = ({
  isOpen,
  onClose,
  consultantId,
  consultant,
  onSuccess
}) => {
  const { t } = useTranslation("portal");
  const toast = useToast();

  const [assessmentName, setAssessmentName] = useState("");
  const [assessmentEmail, setAssessmentEmail] = useState("");
  const [assessmentLanguage, setAssessmentLanguage] = useState("francais");
  const [assessmentJobDescFile, setAssessmentJobDescFile] = useState(null);
  const [assessmentJobMatrix, setAssessmentJobMatrix] = useState(null);
  const [loadingAssessmentJobMatrix, setLoadingAssessmentJobMatrix] = useState(false);
  const [submittingAssessment, setSubmittingAssessment] = useState(false);

  // Assessment matrix selection
  const [assessmentAutoMode, setAssessmentAutoMode] = useState(false);
  const [assessmentTechCompetences, setAssessmentTechCompetences] = useState([]);
  const [assessmentFuncCompetences, setAssessmentFuncCompetences] = useState([]);
  const [assessmentMetierCompetences, setAssessmentMetierCompetences] = useState([]);

  const difficultyChoices = [
    t("asapSkills.form.difficulty.junior"),
    t("asapSkills.form.difficulty.confirme"),
    t("asapSkills.form.difficulty.senior"),
    t("asapSkills.form.difficulty.seniorConfirme"),
    t("asapSkills.form.difficulty.expert")
  ];

  const importanceOptions = [
    {
      value: "primary",
      label: t("asapSkills.form.importance.primary", { defaultValue: "Primary" })
    },
    {
      value: "secondary",
      label: t("asapSkills.form.importance.secondary", { defaultValue: "Secondary" })
    }
  ];

  const MAX_SKILLS = 5;
  const totalSelectedSkills =
    assessmentTechCompetences.length +
    assessmentFuncCompetences.length +
    assessmentMetierCompetences.length;
  const isMaxSkillsReached = totalSelectedSkills >= MAX_SKILLS;

  // Initialize fields when modal opens
  useEffect(() => {
    if (isOpen && consultant) {
      setAssessmentName(consultant.name || "");
      setAssessmentEmail(consultant.email || "");
    }
  }, [isOpen, consultant]);

  // Load skills job matrix for assessments when a job description is uploaded
  useEffect(() => {
    const fetchJobMatrix = async () => {
      if (!assessmentJobDescFile) {
        setAssessmentJobMatrix(null);
        return;
      }
      setLoadingAssessmentJobMatrix(true);
      try {
        const formData = new FormData();
        formData.append("jobDescFile", assessmentJobDescFile);
        const res = await axios.post(
          `${process.env.REACT_APP_BASE_API_URL}/api/job-matrix`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        setAssessmentJobMatrix(res.data || null);
        setAssessmentTechCompetences([]);
        setAssessmentFuncCompetences([]);
        setAssessmentMetierCompetences([]);
      } catch (error) {
        console.error("Failed to load assessment job matrix", error);
        toast({
          title: t("asapSkills.toasts.loadJobMatrixFailed"),
          status: "error"
        });
      } finally {
        setLoadingAssessmentJobMatrix(false);
      }
    };
    fetchJobMatrix();
  }, [assessmentJobDescFile, toast, t]);

  const handleAssessmentSubmit = async () => {
    if (!assessmentEmail.trim()) return;

    if (!assessmentAutoMode && totalSelectedSkills === 0) {
      toast({
        title: t("asapSkills.toasts.noCompetences"),
        status: "error"
      });
      return;
    }

    setSubmittingAssessment(true);
    try {
      const evaluationData = {
        candidate_emails: [assessmentEmail.trim()],
        candidate_names: [assessmentName || ""],
        language: assessmentLanguage,
        auto_mode: assessmentAutoMode,
        consultantId: Number(consultantId)
      };

      if (assessmentJobMatrix?.secteur) {
        evaluationData.secteur = assessmentJobMatrix.secteur;
        if (assessmentJobMatrix.sous_secteur)
          evaluationData.sous_secteur = assessmentJobMatrix.sous_secteur;
        if (assessmentJobMatrix["synthèse_mission"])
          evaluationData.job_desc_summary =
            assessmentJobMatrix["synthèse_mission"];
      }

      if (!assessmentAutoMode) {
        evaluationData.job_matrix = [];
        assessmentTechCompetences.forEach(
          ({ technology, difficulty_level, importance }) => {
          evaluationData.job_matrix.push({
            technology,
            difficulty_level,
            importance,
            type: "technique"
          });
        });
        assessmentFuncCompetences.forEach(
          ({ technology, difficulty_level, importance }) => {
          evaluationData.job_matrix.push({
            technology,
            difficulty_level,
            importance,
            type: "fonctionnel"
          });
        });
        assessmentMetierCompetences.forEach(
          ({ technology, difficulty_level, importance }) => {
            evaluationData.job_matrix.push({
              technology,
              difficulty_level,
              importance,
              type: "metier"
            });
          }
        );
      }

      const formData = new FormData();
      if (assessmentJobDescFile) {
        formData.append("jobDescFile", assessmentJobDescFile);
      }
      formData.append("evaluation_data", JSON.stringify(evaluationData));

      await axios.post(
        `${process.env.REACT_APP_BASE_API_URL}/api/prepare/evaluation`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" }
        }
      );

      toast({
        title: t("pilotConsultants.detail.toasts.assessmentSubmitSuccess"),
        status: "success"
      });

      // Reset form
      setAssessmentJobDescFile(null);
      setAssessmentJobMatrix(null);
      setAssessmentAutoMode(false);
      setAssessmentTechCompetences([]);
      setAssessmentFuncCompetences([]);
      setAssessmentMetierCompetences([]);

      if (onSuccess) onSuccess();
      onClose();
    } catch (e) {
      console.error("Failed to submit assessment for consultant", e);
      toast({
        title: t("pilotConsultants.detail.toasts.assessmentSubmitFailedTitle"),
        description:
          e?.response?.data?.message ||
          t("pilotConsultants.detail.toasts.assessmentSubmitFailedDescription"),
        status: "error"
      });
    } finally {
      setSubmittingAssessment(false);
    }
  };

  // Renders one skill-matrix group (technical / functional / business). The
  // state-shape and update logic are identical across the three groups, so this
  // keeps the per-skill row markup in one place. `items` carries the matrix
  // entries; `nameKey` is the field on each entry holding the skill name.
  const renderSkillSection = (sectionLabel, items, nameKey, selected, setSelected) => (
    <Box>
      <SectionLabel>{sectionLabel}</SectionLabel>
      <VStack align="stretch" spacing="6px">
        {(items || []).map((item, index) => {
          const technology = item[nameKey];
          const current = selected.find((c) => c.technology === technology);
          const defaultImportance =
            item?.importance === "secondary" ? "secondary" : "primary";
          return (
            <HStack key={index} align="center" spacing="10px">
              <Checkbox
                size="sm"
                flex="1"
                colorScheme="blue"
                isChecked={!!current}
                isDisabled={
                  assessmentAutoMode || (isMaxSkillsReached && !current)
                }
                onChange={(e) =>
                  setSelected((prev) =>
                    e.target.checked
                      ? [
                          ...prev,
                          {
                            technology,
                            difficulty_level: difficultyChoices[0],
                            importance: defaultImportance
                          }
                        ]
                      : prev.filter((c) => c.technology !== technology)
                  )
                }
                sx={{ "& .chakra-checkbox__label": { fontSize: "13px" } }}
              >
                {technology}
              </Checkbox>
              <Select
                size="xs"
                maxW="160px"
                borderRadius="8px"
                fontSize="12px"
                value={current?.difficulty_level || difficultyChoices[0]}
                isDisabled={!current || assessmentAutoMode}
                onChange={(e) =>
                  setSelected((prev) =>
                    prev.map((c) =>
                      c.technology === technology
                        ? { ...c, difficulty_level: e.target.value }
                        : c
                    )
                  )
                }
                sx={COMPACT_SELECT_SX}
              >
                {difficultyChoices.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </Select>
              <Select
                size="xs"
                maxW="140px"
                borderRadius="8px"
                fontSize="12px"
                value={current?.importance || defaultImportance}
                isDisabled={!current || assessmentAutoMode}
                onChange={(e) =>
                  setSelected((prev) =>
                    prev.map((c) =>
                      c.technology === technology
                        ? { ...c, importance: e.target.value }
                        : c
                    )
                  )
                }
                sx={COMPACT_SELECT_SX}
              >
                {importanceOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </HStack>
          );
        })}
      </VStack>
    </Box>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay bg="rgba(11,31,58,.45)" />
      <ModalContent sx={MODAL_CONTENT_SX}>
        <ModalHeader sx={MODAL_HEADER_SX} pr="56px">
          <Flex align="center" gap="12px" flexWrap="wrap">
            <Text
              as="span"
              fontSize="17px"
              fontWeight={800}
              letterSpacing="-0.02em"
              color="portal.text"
              sx={{ marginBottom: "0 !important" }}
            >
              {t("pilotConsultants.detail.skills.title")}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody bg="portal.bg" py="14px">
          <VStack align="stretch" spacing="10px">
            <Panel title={t("pilotConsultants.detail.skills.title")}>
              <Box p="12px">
                <Text
                  fontSize="13px"
                  color="portal.muted"
                  mb="12px"
                  sx={{ marginBottom: "12px !important" }}
                >
                  {t("pilotConsultants.detail.skills.description")}
                </Text>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacingX="16px" spacingY="12px">
                  <FormControl>
                    <FieldLabel display="block">
                      {t("pilotConsultants.detail.skills.fields.candidateName")}
                    </FieldLabel>
                    <Input
                      size="sm"
                      borderRadius="10px"
                      value={assessmentName}
                      onChange={(e) => setAssessmentName(e.target.value)}
                    />
                  </FormControl>
                  <FormControl>
                    <FieldLabel display="block">
                      {t("pilotConsultants.detail.skills.fields.candidateEmail")}
                    </FieldLabel>
                    <Input
                      size="sm"
                      borderRadius="10px"
                      type="email"
                      value={assessmentEmail}
                      onChange={(e) => setAssessmentEmail(e.target.value)}
                    />
                  </FormControl>
                  <FormControl>
                    <FieldLabel display="block">
                      {t("pilotConsultants.detail.skills.fields.language")}
                    </FieldLabel>
                    <Select
                      size="sm"
                      borderRadius="10px"
                      value={assessmentLanguage}
                      onChange={(e) => setAssessmentLanguage(e.target.value)}
                    >
                      <option value="francais">
                        {t("asapSkills.form.languageOptions.fr")}
                      </option>
                      <option value="anglais">
                        {t("asapSkills.form.languageOptions.en")}
                      </option>
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FieldLabel display="block">
                      {t("pilotConsultants.detail.skills.fields.jobDescFile")}
                    </FieldLabel>
                    {assessmentJobDescFile ? (
                      <Tag
                        size="md"
                        variant="subtle"
                        colorScheme="blue"
                        borderRadius="999px"
                      >
                        <TagLabel>{assessmentJobDescFile.name}</TagLabel>
                        <TagCloseButton
                          onClick={() => setAssessmentJobDescFile(null)}
                        />
                      </Tag>
                    ) : (
                      <Button
                        size="sm"
                        variant="secondary"
                        leftIcon={<FaCloudUploadAlt />}
                        onClick={() => {
                          const input = document.createElement("input");
                          input.type = "file";
                          input.accept = ".pdf,.doc,.docx";
                          input.onchange = (e) => {
                            const file = e.target.files?.[0];
                            if (file) setAssessmentJobDescFile(file);
                          };
                          input.click();
                        }}
                      >
                        {t("pilotConsultants.detail.skills.actions.selectJobDesc")}
                      </Button>
                    )}
                  </FormControl>
                </SimpleGrid>
              </Box>
            </Panel>

            {loadingAssessmentJobMatrix ? (
              <Panel>
                <Center py="24px">
                  <Spinner size="sm" color="portal.primary" />
                </Center>
              </Panel>
            ) : assessmentJobMatrix ? (
              <Panel
                title={t("asapSkills.form.evaluationSettingsTitle")}
                actions={
                  <HStack spacing="8px">
                    <FieldLabel sx={{ marginBottom: "0 !important" }}>
                      {t("asapSkills.form.autoModeLabel")}
                    </FieldLabel>
                    <Switch
                      colorScheme="blue"
                      isChecked={assessmentAutoMode}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setAssessmentAutoMode(checked);
                        if (checked) {
                          setAssessmentTechCompetences([]);
                          setAssessmentFuncCompetences([]);
                          setAssessmentMetierCompetences([]);
                        }
                      }}
                    />
                  </HStack>
                }
              >
                <Box p="12px">
                  {!assessmentAutoMode && isMaxSkillsReached && (
                    <Alert
                      status="warning"
                      borderRadius="10px"
                      mb="12px"
                      alignItems="center"
                      fontSize="13px"
                    >
                      <AlertIcon />
                      <Text fontSize="13px" sx={{ marginBottom: "0 !important" }}>
                        {t("asapSkills.alerts.maxSkillsReached")}
                      </Text>
                    </Alert>
                  )}

                  <VStack align="stretch" spacing="16px">
                    {renderSkillSection(
                      t("asapSkills.form.sections.technical"),
                      assessmentJobMatrix.MATRICE_TECHNIQUES,
                      "compétence_technique",
                      assessmentTechCompetences,
                      setAssessmentTechCompetences
                    )}
                    {renderSkillSection(
                      t("asapSkills.form.sections.functional"),
                      assessmentJobMatrix.MATRICE_FONCTIONNELLES,
                      "compétence_fonctionnelle",
                      assessmentFuncCompetences,
                      setAssessmentFuncCompetences
                    )}
                    {renderSkillSection(
                      t("asapSkills.form.sections.business"),
                      assessmentJobMatrix.MATRICE_METIER,
                      "compétence_metier",
                      assessmentMetierCompetences,
                      setAssessmentMetierCompetences
                    )}
                  </VStack>
                </Box>
              </Panel>
            ) : null}
          </VStack>
        </ModalBody>
        <ModalFooter sx={MODAL_FOOTER_SX}>
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t("pilotConsultants.form.buttons.cancel")}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleAssessmentSubmit}
            isDisabled={
              !assessmentEmail || (!assessmentAutoMode && totalSelectedSkills === 0)
            }
            isLoading={submittingAssessment}
          >
            {t("pilotConsultants.detail.skills.actions.submit")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ConsultantAssessmentModal;
